var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/Buttons/DarkModeIconButton.tsx
import { DarkModeRounded, LightModeRounded } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { RotationAnimation } from "@xylabs/react-animation";
import { FlexCol } from "@xylabs/react-flexbox";
import React, { useState } from "react";
var DarkModeIconButton = /* @__PURE__ */ __name(({ darkMode, defaultDarkModeColor, defaultLightModeColor, toggleMode, ...props }) => {
  const [iconColor, setIconColor] = useState(darkMode ? defaultDarkModeColor : defaultLightModeColor);
  const [iconColorIsSet, setIconColorIsSet] = useState(false);
  const handleHover = /* @__PURE__ */ __name(() => {
    setIconColor(() => {
      const darkModeIconColor = defaultDarkModeColor ?? "inherit";
      const lightModeIconColor = defaultLightModeColor ?? "inherit";
      if (darkMode) {
        return iconColorIsSet ? darkModeIconColor : "info";
      } else {
        return iconColorIsSet ? lightModeIconColor : "warning";
      }
    });
    setIconColorIsSet(!iconColorIsSet);
  }, "handleHover");
  const handleDarkModeChange = /* @__PURE__ */ __name(() => {
    toggleMode?.();
  }, "handleDarkModeChange");
  return /* @__PURE__ */ React.createElement(FlexCol, null, /* @__PURE__ */ React.createElement(RotationAnimation, {
    rotation: 20
  }, /* @__PURE__ */ React.createElement(IconButton, {
    color: iconColor,
    onClick: handleDarkModeChange,
    onMouseEnter: /* @__PURE__ */ __name(() => handleHover(), "onMouseEnter"),
    onMouseLeave: /* @__PURE__ */ __name(() => handleHover(), "onMouseLeave"),
    ...props
  }, darkMode ? /* @__PURE__ */ React.createElement(DarkModeRounded, null) : /* @__PURE__ */ React.createElement(LightModeRounded, null))));
}, "DarkModeIconButton");

// src/Buttons/DarkModeIconButtonForColorScheme.tsx
import { DarkModeRounded as DarkModeRounded2, LightModeRounded as LightModeRounded2 } from "@mui/icons-material";
import { IconButton as IconButton2, useColorScheme } from "@mui/material";
import { RotationAnimation as RotationAnimation2 } from "@xylabs/react-animation";
import { FlexCol as FlexCol2 } from "@xylabs/react-flexbox";
import React2, { useMemo, useState as useState2 } from "react";
var DarkModeIconButtonForColorScheme = /* @__PURE__ */ __name(({ defaultDarkModeColor, defaultLightModeColor, ...props }) => {
  const { mode, setMode, systemMode } = useColorScheme();
  const resolvedMode = useMemo(() => (mode === "system" ? systemMode : mode) ?? "light", [
    mode,
    systemMode
  ]);
  const [iconColor, setIconColor] = useState2(() => resolvedMode === "dark" ? defaultDarkModeColor : defaultLightModeColor);
  const [iconColorIsSet, setIconColorIsSet] = useState2(false);
  const toggleMode = /* @__PURE__ */ __name(() => {
    if (mode === "light") {
      setMode("dark");
    } else if (mode === "dark") {
      setMode("system");
    } else {
      setMode("light");
    }
  }, "toggleMode");
  const handleHover = /* @__PURE__ */ __name(() => {
    setIconColor(() => {
      const darkModeIconColor = defaultDarkModeColor ?? "inherit";
      const lightModeIconColor = defaultLightModeColor ?? "inherit";
      if (resolvedMode === "dark") {
        return iconColorIsSet ? darkModeIconColor : "info";
      } else {
        return iconColorIsSet ? lightModeIconColor : "warning";
      }
    });
    setIconColorIsSet(!iconColorIsSet);
  }, "handleHover");
  return /* @__PURE__ */ React2.createElement(FlexCol2, null, /* @__PURE__ */ React2.createElement(RotationAnimation2, {
    rotation: 20
  }, /* @__PURE__ */ React2.createElement(IconButton2, {
    color: iconColor,
    onClick: toggleMode,
    onMouseEnter: /* @__PURE__ */ __name(() => handleHover(), "onMouseEnter"),
    onMouseLeave: /* @__PURE__ */ __name(() => handleHover(), "onMouseLeave"),
    ...props
  }, resolvedMode === "dark" ? /* @__PURE__ */ React2.createElement(DarkModeRounded2, null) : /* @__PURE__ */ React2.createElement(LightModeRounded2, null), mode === "system" ? /* @__PURE__ */ React2.createElement("span", {
    style: {
      position: "absolute",
      bottom: 2,
      fontSize: 8
    }
  }, "Auto") : null)));
}, "DarkModeIconButtonForColorScheme");

// src/Buttons/ThemeModeButtonGroup.tsx
import { Button, ButtonGroup, useColorScheme as useColorScheme2 } from "@mui/material";
import React3 from "react";
var ThemeModeButtonGroup = /* @__PURE__ */ __name((props) => {
  const { mode, setMode } = useColorScheme2();
  const darkMode = mode === "dark";
  const lightMode = mode === "light";
  const systemMode = mode === "system";
  return /* @__PURE__ */ React3.createElement(ButtonGroup, props, /* @__PURE__ */ React3.createElement(Button, {
    variant: darkMode ? "contained" : "outlined",
    onClick: /* @__PURE__ */ __name(() => setMode("dark"), "onClick")
  }, "Dark Mode"), /* @__PURE__ */ React3.createElement(Button, {
    variant: lightMode ? "contained" : "outlined",
    onClick: /* @__PURE__ */ __name(() => setMode("light"), "onClick")
  }, "Light Mode"), /* @__PURE__ */ React3.createElement(Button, {
    variant: systemMode ? "contained" : "outlined",
    onClick: /* @__PURE__ */ __name(() => setMode("system"), "onClick")
  }, "System"));
}, "ThemeModeButtonGroup");

// src/InvertibleMuiThemeProvider/InvertibleMuiThemeProvider.tsx
import { createTheme, responsiveFontSizes, ThemeProvider, useColorScheme as useColorScheme3 } from "@mui/material";
import React4, { useEffect, useMemo as useMemo2 } from "react";
var SyncMode = /* @__PURE__ */ __name(({ defaultMode }) => {
  const { setMode } = useColorScheme3();
  useEffect(() => {
    if (defaultMode) setMode(defaultMode);
  }, [
    defaultMode
  ]);
  return /* @__PURE__ */ React4.createElement(React4.Fragment, null);
}, "SyncMode");
var InvertibleCssVarsProvider = /* @__PURE__ */ __name(({ children, defaultMode, noResponsiveFontSizes, theme }) => {
  const updatedTheme = useMemo2(() => theme ? noResponsiveFontSizes ? theme : responsiveFontSizes(theme) : createTheme(), [
    noResponsiveFontSizes,
    theme
  ]);
  return /* @__PURE__ */ React4.createElement(ThemeProvider, {
    theme: updatedTheme
  }, /* @__PURE__ */ React4.createElement(SyncMode, defaultMode ? {
    defaultMode
  } : {}), children);
}, "InvertibleCssVarsProvider");
var InvertibleMuiThemeProvider = InvertibleCssVarsProvider;

// src/InvertibleMuiThemeProvider/useColorSchemeEx.tsx
import { useColorScheme as useColorScheme4 } from "@mui/material";
var useColorSchemeEx = /* @__PURE__ */ __name(() => {
  return useColorScheme4();
}, "useColorSchemeEx");

// src/LegacyInvertibleThemeProvider/InvertibleThemeContext.tsx
import { createContext } from "react";
var InvertibleThemeContext = /* @__PURE__ */ createContext({
  options: {}
});

// src/LegacyInvertibleThemeProvider/InvertibleThemeProvider.tsx
import { createTheme as createTheme3, responsiveFontSizes as responsiveFontSizes2, ScopedCssBaseline, ThemeProvider as ThemeProvider2 } from "@mui/material";
import deepmerge from "deepmerge";
import React5, { useMemo as useMemo3 } from "react";
import rfdc2 from "rfdc";

// src/LegacyInvertibleThemeProvider/resolveThemeColors.ts
import { createTheme as createTheme2 } from "@mui/material";
import rfdc from "rfdc";
var resolveThemeColors = /* @__PURE__ */ __name((options) => {
  const theme = createTheme2(options);
  const clone = rfdc();
  return {
    ...clone(options),
    palette: {
      text: {
        primary: theme.palette?.getContrastText(theme.palette.primary.main),
        secondary: theme.palette?.getContrastText(theme.palette.secondary.main)
      }
    }
  };
}, "resolveThemeColors");

// src/LegacyInvertibleThemeProvider/use.ts
import { use } from "react";
var useInvertibleThemeProvider = /* @__PURE__ */ __name(() => {
  return use(InvertibleThemeContext);
}, "useInvertibleThemeProvider");

// src/LegacyInvertibleThemeProvider/InvertibleThemeProvider.tsx
var InvertibleThemeProvider = /* @__PURE__ */ __name(({ options, children, dark, resolve = false, scoped = false, invert = false, noResponsiveFonts, darkTheme, darkOptions, lightOptions }) => {
  const clone = rfdc2();
  const parentContext = useInvertibleThemeProvider();
  const clonedOptions = clone(options ?? parentContext.options ?? {});
  const clonedDarkOptions = clone(darkOptions ?? darkTheme ?? parentContext.darkOptions);
  const clonedLightOptions = clone(lightOptions ?? parentContext.lightOptions);
  clonedOptions.palette = clonedOptions.palette ?? {};
  if (invert) {
    clonedOptions.palette.mode = clonedOptions.palette.mode === "dark" ? "light" : "dark";
  }
  if (dark !== void 0) {
    clonedOptions.palette.mode = dark ? "dark" : "light";
  }
  const modeOptions = (clonedOptions.palette.mode === "dark" ? clonedDarkOptions : clonedLightOptions) ?? {};
  let themeOptions = deepmerge(clonedOptions, modeOptions);
  if (resolve) {
    themeOptions = resolveThemeColors(themeOptions);
  }
  const theme = noResponsiveFonts ? createTheme3(themeOptions) : responsiveFontSizes2(createTheme3(themeOptions));
  const value = useMemo3(() => ({
    darkOptions: clonedDarkOptions,
    lightOptions: clonedLightOptions,
    options: clonedOptions
  }), [
    clonedDarkOptions,
    clonedLightOptions,
    clonedOptions
  ]);
  return scoped ? /* @__PURE__ */ React5.createElement(ScopedCssBaseline, null, /* @__PURE__ */ React5.createElement(InvertibleThemeContext, {
    value
  }, /* @__PURE__ */ React5.createElement(ThemeProvider2, {
    theme
  }, children))) : /* @__PURE__ */ React5.createElement(InvertibleThemeContext, {
    value
  }, /* @__PURE__ */ React5.createElement(ThemeProvider2, {
    theme
  }, children));
}, "InvertibleThemeProvider");
export {
  DarkModeIconButton,
  DarkModeIconButtonForColorScheme,
  InvertibleCssVarsProvider,
  InvertibleMuiThemeProvider,
  InvertibleThemeContext,
  InvertibleThemeProvider,
  SyncMode,
  ThemeModeButtonGroup,
  useColorSchemeEx,
  useInvertibleThemeProvider
};
//# sourceMappingURL=index.mjs.map